import { Chip } from '@design/components/chip/Chip';
import { DatePicker, DatePickerLocales } from '@design/components/date-picker/DatePicker';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { Icon } from '@design/components/icon/Icon';
import {
  formatDateToLocale,
  Formats,
  InternationalDateFormats,
} from '@helpers';
import { Option } from '@terminal/components/filter/Option';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import clsx from 'clsx';
import {
  addDays,
  format,
  isDate,
  isToday,
  isYesterday,
  startOfDay,
  startOfYesterday,
} from 'date-fns';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './DatePillDropdown.module.scss';

export interface DatePillDropdownAction {
  date?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface DatePillDropdownProps {
  originalPillTime: string;
  onChangeDate: (action: DatePillDropdownAction) => void;
  minDate?: Date;
  className?: string;
  dateFormat?: keyof typeof Formats;
  availableDates?: Set<string>;
  selectRange?: boolean;
}

export const DatePillDropdown = (({
  originalPillTime,
  onChangeDate,
  minDate,
  className,
  dateFormat = 'EEEE, dd MMMM',
  availableDates,
  selectRange = false,
}: DatePillDropdownProps) => {
  const { t } = useTranslation(['news', 'datePicker']);

  const [rangeValue, setRangeValue] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const datePickerLocales: DatePickerLocales = {
    selectYear: t('datePicker:select_year'),
    selectMonth: t('datePicker:select_month'),
    selectDecade: t('datePicker:select_decade'),
    previousMonth: t('datePicker:previous_month'),
    previousYear: t('datePicker:previous_year'),
    previousDecade: t('datePicker:previous_decade'),
    nextMonth: t('datePicker:next_month'),
    nextYear: t('datePicker:next_year'),
    nextDecade: t('datePicker:next_decade'),
  };

  const [isShowDatePicker, setIsShowDatePicker] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const ref = useRef<HTMLButtonElement>(null);

  const pillLabel = () => {
    const labelTime = new Date(originalPillTime);

    if (isToday(labelTime)) {
      return t('news:pillDropdown.today');
    } else if (isYesterday(labelTime)) {
      return t('news:pillDropdown.yesterday');
    } else {
      if (languageSetting === 'ja') {
        return formatDateToLocale(
          languageSetting,
          labelTime,
          Formats[dateFormat]
        );
      } else {
        return new Intl.DateTimeFormat(
          navigator.language,
          InternationalDateFormats[dateFormat]
        ).format(labelTime);
      }
    }
  };

  const todayDate = new Date();
  const yesterdayDate = startOfYesterday();
  const todayKey = format(todayDate, 'yyyy-MM-dd');
  const yesterdayKey = format(yesterdayDate, 'yyyy-MM-dd');

  const handleRangeDateChange = (newDate: [Date | null, Date | null]) => {
    const [a, b] = newDate;

    if (a && b) {
      const start = startOfDay(a);
      const end = startOfDay(b);
      const [from, to] =
        start.getTime() <= end.getTime() ? [start, end] : [end, start];
      onChangeDate({
        dateFrom: from.toISOString(),
        dateTo: addDays(to, 1).toISOString(),
      });
      setRangeValue([null, null]);
      setIsDropdownOpen(false);
    } else {
      setRangeValue([a ?? null, b ?? null]);
    }
  };

  const isTodayAvailable = !availableDates || availableDates.has(todayKey);
  const isYesterdayAvailable =
    !availableDates || availableDates.has(yesterdayKey);

  const pillOptions = [
    {
      key: 'today',
      name: t('news:pillDropdown.today'),
      value: { value: todayDate },
      isChecked: originalPillTime ? isToday(new Date(originalPillTime)) : false,
      disabled: !isTodayAvailable,
    },
    {
      key: 'yesterday',
      name: t('news:pillDropdown.yesterday'),
      value: { value: yesterdayDate },
      isChecked: originalPillTime
        ? isYesterday(new Date(originalPillTime))
        : false,
      disabled: !isYesterdayAvailable,
    },
  ];

  return (
    <div className={className}>
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={() => {
          setIsShowDatePicker(false);
          setIsDropdownOpen(prev => !prev);
        }}
        triggerNode={
          <div className={styles.chipWrapper} data-chip-wrapper>
            <Chip
              ref={ref}
              label={pillLabel()}
              rightIcon="ChevronLightDown"
              rightIconSize={12}
              data-testid="date-pill-chip"
            />
          </div>
        }
        contentProps={{
          asChild: true,
        }}
      >
        <div
          className={clsx(styles.menuWrapper, {
            [styles.isCalendarShow]: isShowDatePicker,
          })}
        >
          {isShowDatePicker ? (
            <DatePicker
              value={selectRange ? rangeValue : new Date(originalPillTime)}
              selectRange={selectRange}
              maxDate={new Date()}
              minDate={minDate}
              onDateChange={newDate => {
                if (selectRange && Array.isArray(newDate)) {
                  handleRangeDateChange(newDate);
                } else if (!selectRange && newDate && isDate(newDate)) {
                  onChangeDate({
                    date: newDate.toISOString(),
                  });
                  setIsDropdownOpen(false);
                }
              }}
              locales={datePickerLocales}
              data-testid="calendar"
            />
          ) : (
            <>
              {pillOptions.map(option => {
                return (
                  <Option
                    key={option.key}
                    option={option}
                    handleClick={() => {
                      if (!option.disabled) {
                        onChangeDate({
                          date: option.value.value.toISOString(),
                        });
                      }
                    }}
                    isChecked={option.isChecked}
                    disabled={option.disabled}
                    data-testid={`time-${option.key}`}
                  />
                );
              })}
              <hr className={styles.divider} />
              <div
                className={styles.customDate}
                onClick={() => {
                  if (selectRange) {
                    setRangeValue([null, null]);
                  }
                  setIsShowDatePicker(true);
                }}
                data-testid="date-picker-opener"
              >
                <Icon iconName="Settings" size={16} />
                <span className={styles.specificDate}>
                  {t('news:pillDropdown.specificDate')}
                </span>
              </div>
            </>
          )}
        </div>
      </DropdownMenu>
    </div>
  );
}) as React.FC<DatePillDropdownProps>;
